import { ComponentType } from 'react';
import { RemoteAppSettings } from 'types/micro-frontends';
import {
  MFA_ARTIFACTS_COOKIE,
  SET_MFA_BRANCH,
  SET_MFA_TAG
} from 'common/constants/constants';
import Cookies from 'js-cookie';

const MFA_SUBDOMAIN = 'mfa.';
const REMOTE_ENTRY = 'remoteEntry.js';

const getMfaVersion = (app: RemoteAppSettings, value: string) => {
  return value
    ? value
        .split(';')
        .map(item => {
          const [key, value] = item.split(':');
          return key === app.artifactPath ? value : '';
        })
        .join('')
    : '';
};

const extractVersionParam = (app: RemoteAppSettings) => {
  const cookieKey = `${app.scope}_${MFA_ARTIFACTS_COOKIE}`;
  const qsParams = new URLSearchParams(window.location.search);
  const branchParam = getMfaVersion(app, qsParams.get(SET_MFA_BRANCH));
  const tagParam = getMfaVersion(app, qsParams.get(SET_MFA_TAG));

  const artifactPath = tagParam
    ? `/${app.artifactPath}/tags/${tagParam}`
    : branchParam
    ? `/${app.artifactPath}/branches/${branchParam}`
    : '';
  if (artifactPath) {
    Cookies.set(cookieKey, artifactPath, {
      expires: 2
    });
  } else {
    return Cookies.get(cookieKey) || '';
  }
  return artifactPath;
};

export function resolveRemoteUrl(
  env: string,
  location: Location,
  app: RemoteAppSettings,
  bustCache = false
) {
  const timestamp = bustCache
    ? `&${Math.random().toString().slice(2, 16)}`
    : '';
  const tag = extractVersionParam(app);

  return env === 'development'
    ? `${app.localUrl}/${REMOTE_ENTRY}?${timestamp}`
    : `${location.protocol}//${location.hostname
        .replace('app.', MFA_SUBDOMAIN)
        .replace('app2.', MFA_SUBDOMAIN)
        .replace('app3.', MFA_SUBDOMAIN)}/${
        app.scope
      }${tag}/${REMOTE_ENTRY}?${timestamp}`;
}
/**
 * Loads a remote application module
 * See [Dynamic Remote Containers](https://webpack.js.org/concepts/module-federation/#dynamic-remote-containers)
 * for more information on how this works
 * @param url Remote app URL
 * @param app Remote app settings
 * @returns Remote module
 */
export function loadRemoteApp(url: string, app: RemoteAppSettings) {
  const { scope, module } = app;

  return (): Promise<{ default: ComponentType<any> }> =>
    new Promise((resolve, reject) => {
      const element = document.createElement('script');

      element.src = url;
      element.type = 'text/javascript';
      element.async = true;

      element.onload = async () => {
        // Initializes the share scope.
        const container = window[scope];
        // Initialize the container, it may provide shared modules
        await container.init(__webpack_share_scopes__.default);
        const factory = await window[scope].get(module);
        const Module = factory();

        resolve(Module);
      };

      element.onerror = err => {
        reject(
          new Error(
            `Failed to initialize remote Application\nURL: ${url}\nScope: ${scope}\nModule: ${module}`,
            { cause: err }
          )
        );
      };

      document.head.appendChild(element);
    });
}
